import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { HKPImage } from "../../../media/HKPImage";
import { compressImage } from "../../../media/imageCompression";
import { CLOSED_CODES, fetchShadeView, uploadShadePhoto } from "./shadeApi";
import type { ShadeView } from "./shadeApi";
import { loadQueued, removeQueued, saveQueued } from "./shadeQueue";
import { SHADE_STRINGS as S } from "./shadeStrings";
import "./shade-upload.scss";

type TileState = "preparing" | "queued" | "uploading" | "sent" | "waiting" | "failed" | "rejected";
type Tile = { id: string; previewUrl: string; blob?: Blob; state: TileState; progress: number; message?: string };

const ACTIVE: TileState[] = ["preparing", "queued", "uploading", "waiting", "failed"];

const newId = (): string => {
  const c: any = typeof crypto !== "undefined" ? crypto : undefined;
  if (c?.randomUUID) return c.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

const preview = (blob: Blob): string => {
  try {
    return typeof URL !== "undefined" && URL.createObjectURL ? URL.createObjectURL(blob) : "";
  } catch {
    return "";
  }
};

/**
 * /shade/:token — a worker sends photos of one piece in its current (dyed) colour from a phone. No login and
 * no app chrome. Camera or gallery, up to the link's limit; each photo uploads with its own progress and retry,
 * and waits on the phone (IndexedDB) when there is no signal. Shows no price and no other photos.
 */
const ShadeUploadPage = () => {
  const { token = "" } = useParams<{ token: string }>();
  const [view, setView] = useState<ShadeView | null>(null);
  const [closed, setClosed] = useState<string | null>(null);
  const [tiles, setTiles] = useState<Tile[]>([]);
  const [sentNow, setSentNow] = useState(0);
  const [doneDismissed, setDoneDismissed] = useState(false);
  const [notice, setNotice] = useState("");
  const [offline, setOffline] = useState(false);
  const tilesRef = useRef<Tile[]>([]);
  const busy = useRef(false);
  const closedRef = useRef<string | null>(null);
  const cameraRef = useRef<HTMLInputElement | null>(null);
  const galleryRef = useRef<HTMLInputElement | null>(null);

  const update = useCallback((id: string, patch: Partial<Tile>) => {
    tilesRef.current = tilesRef.current.map((t) => (t.id === id ? { ...t, ...patch } : t));
    setTiles(tilesRef.current);
  }, []);

  const close = useCallback((code: string) => {
    closedRef.current = code;
    setClosed(code);
  }, []);

  const pump = useCallback(async () => {
    if (busy.current || closedRef.current) return;
    const next = tilesRef.current.find((t) => t.state === "queued" && t.blob);
    if (!next || !next.blob) return;
    busy.current = true;
    update(next.id, { state: "uploading", progress: 0, message: undefined });
    const res = await uploadShadePhoto(token, next.blob, next.id, (p) => update(next.id, { progress: p }));
    busy.current = false;
    const code = res.code || "";
    if (res.success && res.data) {
      const d = res.data;
      update(next.id, { state: "sent", progress: 100, blob: undefined });
      void removeQueued(next.id);
      setSentNow((n) => n + 1);
      setOffline(false);
      setView((v) => (v ? { ...v, used: d.used, remaining: d.remaining, maxUploads: d.maxUploads } : v));
    } else if (res.status === 0) {
      update(next.id, { state: "waiting", progress: 0, message: S.waiting });
      setOffline(true);
      return;
    } else if ((CLOSED_CODES as readonly string[]).includes(code)) {
      update(next.id, { state: "failed", message: S.closed[code]?.title });
      tilesRef.current.filter((t) => ACTIVE.includes(t.state)).forEach((t) => void removeQueued(t.id));
      close(code);
      return;
    } else if (code === "UNSUPPORTED_TYPE" || code === "TOO_LARGE") {
      update(next.id, { state: "rejected", blob: undefined, message: code === "TOO_LARGE" ? S.rejectedSize : S.rejectedType });
      void removeQueued(next.id);
    } else if (res.status === 429) {
      update(next.id, { state: "queued", message: S.waiting });
      setTimeout(() => void pump(), 5000);
      return;
    } else {
      update(next.id, { state: "failed", message: S.failed });
    }
    void pump();
  }, [token, update, close]);

  // Open the link, then pick up anything still waiting on this phone from last time.
  useEffect(() => {
    let alive = true;
    (async () => {
      const res = await fetchShadeView(token);
      if (!alive) return;
      if (res.success && res.data) {
        setView(res.data);
        if (res.data.remaining <= 0) close("LINK_FULL");
        const saved = await loadQueued(token);
        if (!alive || !saved.length) return;
        tilesRef.current = saved.map((q) => ({ id: q.id, blob: q.blob, previewUrl: preview(q.blob), state: "queued" as TileState, progress: 0 }));
        setTiles(tilesRef.current);
        void pump();
      } else if (res.status === 0) {
        setOffline(true);
        close("UNAVAILABLE");
      } else {
        close((CLOSED_CODES as readonly string[]).includes(res.code || "") ? (res.code as string) : res.status === 404 ? "NOT_VALID" : "UNAVAILABLE");
      }
    })();
    return () => {
      alive = false;
    };
  }, [token, pump, close]);

  // Back online: everything that was waiting goes again.
  useEffect(() => {
    const back = () => {
      tilesRef.current = tilesRef.current.map((t) => (t.state === "waiting" ? { ...t, state: "queued" as TileState, message: undefined } : t));
      setTiles(tilesRef.current);
      setOffline(false);
      void pump();
    };
    window.addEventListener("online", back);
    return () => window.removeEventListener("online", back);
  }, [pump]);

  const inFlight = tiles.filter((t) => ACTIVE.includes(t.state)).length;
  const left = Math.max(0, (view?.remaining ?? 0) - inFlight);

  const onFiles = async (files: FileList | null) => {
    if (!files || !files.length || closedRef.current) return;
    const list = Array.from(files);
    const take = list.slice(0, left);
    setNotice(list.length > take.length ? S.tooMany(left) : "");
    setDoneDismissed(true);
    for (const file of take) {
      const id = newId();
      tilesRef.current = [...tilesRef.current, { id, previewUrl: preview(file), state: "preparing", progress: 0 }];
      setTiles(tilesRef.current);
      try {
        const master = await compressImage(file);
        await saveQueued({ id, token, blob: master.blob, createdAt: Date.now() });
        update(id, { blob: master.blob, state: "queued" });
      } catch (e: any) {
        const unsupported = /format|decode|HEIC/i.test(String(e?.message || ""));
        update(id, { state: "rejected", message: unsupported ? S.rejectedType : S.rejectedPrepare });
      }
      void pump();
    }
    setDoneDismissed(false);
  };

  const retry = (id: string) => {
    update(id, { state: "queued", message: undefined });
    void pump();
  };

  const dismiss = (id: string) => {
    void removeQueued(id);
    tilesRef.current = tilesRef.current.filter((t) => t.id !== id);
    setTiles(tilesRef.current);
  };

  const done = sentNow > 0 && inFlight === 0 && !doneDismissed;
  const closedCopy = closed ? S.closed[closed] || S.closed.UNAVAILABLE : null;

  return (
    <div className="shade-up">
      <header className="shade-up__top">
        <span className="shade-up__brand">{S.brand}</span>
        <span className="shade-up__eyebrow">{S.eyebrow}</span>
      </header>
      <main className="shade-up__main">
        {!view && !closed ? (
          <p className="shade-up__status" role="status">
            {S.opening}
          </p>
        ) : null}

        {view ? (
          <section className="shade-up__card" aria-label="Piece">
            <div className="shade-up__cover">
              {view.coverUrl ? <HKPImage preset="THUMBNAIL" legacyUrl={view.coverUrl} alt={view.productName || "Piece"} loading="eager" /> : null}
            </div>
            <div className="shade-up__who">
              <h1 className="shade-up__name">{view.productName}</h1>
              {view.serial ? <span className="shade-up__serial">{S.serial(view.serial)}</span> : null}
              {view.colour ? <span className="shade-up__shade">{view.dyed ? S.dyedColour(view.colour) : S.colour(view.colour)}</span> : null}
            </div>
          </section>
        ) : null}

        {closedCopy ? (
          <section className="shade-up__closed" role="alert" data-closed={closed}>
            <h2>{closedCopy.title}</h2>
            <p>{closedCopy.body}</p>
            {closed === "UNAVAILABLE" ? (
              <button type="button" className="shade-up__btn is-ghost" onClick={() => window.location.reload()}>
                {S.tryAgain}
              </button>
            ) : null}
          </section>
        ) : null}

        {view && !closed ? (
          <>
            <p className="shade-up__counter" aria-live="polite">
              {S.counter(view.used, view.maxUploads, left)}
            </p>
            {done ? (
              <section className="shade-up__done" role="status">
                <p>{S.doneTitle(sentNow)}</p>
                <button type="button" className="shade-up__btn is-ghost" onClick={() => setDoneDismissed(true)}>
                  {S.addMore}
                </button>
              </section>
            ) : (
              <div className="shade-up__actions">
                <button type="button" className="shade-up__btn" disabled={left <= 0} onClick={() => cameraRef.current?.click()}>
                  {S.takePhoto}
                </button>
                <button type="button" className="shade-up__btn is-ghost" disabled={left <= 0} onClick={() => galleryRef.current?.click()}>
                  {S.chooseGallery}
                </button>
                <input
                  ref={cameraRef}
                  className="shade-up__file"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  multiple
                  tabIndex={-1}
                  aria-hidden="true"
                  data-testid="shade-camera"
                  onChange={(e) => {
                    void onFiles(e.target.files);
                    e.target.value = "";
                  }}
                />
                <input
                  ref={galleryRef}
                  className="shade-up__file"
                  type="file"
                  accept="image/*"
                  multiple
                  tabIndex={-1}
                  aria-hidden="true"
                  data-testid="shade-gallery"
                  onChange={(e) => {
                    void onFiles(e.target.files);
                    e.target.value = "";
                  }}
                />
              </div>
            )}
            <p className="shade-up__hint">{S.hint}</p>
            {offline ? (
              <p className="shade-up__notice" role="status">
                {S.offline}
              </p>
            ) : null}
            {notice ? (
              <p className="shade-up__notice" role="status">
                {notice}
              </p>
            ) : null}
          </>
        ) : null}

        {tiles.length ? (
          <ul className="shade-up__grid" aria-label="Photos">
            {tiles.map((t, i) => (
              <li key={t.id} className={`shade-up__tile is-${t.state}`}>
                <div className="shade-up__img">
                  {t.previewUrl ? <HKPImage preset="THUMBNAIL" localPreviewUrl={t.previewUrl} alt={`Photo ${i + 1}`} /> : null}
                  {t.state === "uploading" ? (
                    <span className="shade-up__bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={t.progress} aria-label={`Photo ${i + 1} sending`}>
                      <span style={{ transform: `scaleX(${t.progress / 100})` }} />
                    </span>
                  ) : null}
                </div>
                <span className="shade-up__state">
                  {t.state === "sent" ? S.sent : t.state === "uploading" || t.state === "queued" || t.state === "preparing" ? S.sending : t.message || S.failed}
                </span>
                {t.state === "failed" && !closed ? (
                  <button type="button" className="shade-up__mini" onClick={() => retry(t.id)}>
                    {S.retry}
                  </button>
                ) : null}
                {t.state === "rejected" ? (
                  <button type="button" className="shade-up__mini" onClick={() => dismiss(t.id)}>
                    {S.remove}
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
      </main>
    </div>
  );
};

export default ShadeUploadPage;
